// src/stores/commsNotificationStore.js
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { collection } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { useUserStore } from '@/stores/userStore'

export const useCommsNotificationStore = defineStore('commsNotification', () => {
  const unreadCommsByCustomerId = ref({})
  const isSubscribed = ref(false)
  let unsubscribe = null

  const userStore = useUserStore()

  const totalUnreadCount = computed(() => {
    return Object.values(unreadCommsByCustomerId.value).reduce((total, customer) => {
      return (
        total +
        (customer.unreadTextCount || 0) +
        (customer.unreadEmailCount || 0) +
        (customer.unreadPhoneCount || 0) +
        (customer.unreadChatCount || 0)
      )
    }, 0)
  })

  const customerIdsWithUnreadComms = computed(
    () => new Set(Object.keys(unreadCommsByCustomerId.value))
  )

  // --- START DIAGNOSTIC LOGGING ---
  watch(
    unreadCommsByCustomerId,
    newValue => {
      console.log(
        '[CommsNotificationStore] State changed: unreadCommsByCustomerId',
        JSON.parse(JSON.stringify(newValue))
      )
    },
    { deep: true }
  )

  watch(totalUnreadCount, newCount => {
    console.log(
      `[CommsNotificationStore] Computed property changed: totalUnreadCount is now ${newCount}`
    )
  })
  // --- END DIAGNOSTIC LOGGING ---

  function subscribe() {
    const uid = userStore.user?.uid
    if (!uid) return

    // If already subscribed, verify the subscription is for the current user
    if (isSubscribed.value && unsubscribe) {
      console.log(`[CommsNotificationStore] Already subscribed for user: ${uid}`)
      return
    }

    // Clean up any stale subscription
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }

    console.log(`[CommsNotificationStore] Subscribing to unread comms for user: ${uid}`)

    const unreadCommsRef = collection(db, 'users', uid, 'unreadComms')

    unsubscribe = onSnapshot(
      unreadCommsRef,
      snapshot => {
        console.log(
          `[CommsNotificationStore] onSnapshot fired with ${snapshot.size} unread documents.`
        )
        const next = {}
        snapshot.forEach(doc => {
          next[doc.id] = doc.data() || {}
        })
        unreadCommsByCustomerId.value = next
      },
      error => {
        console.error('[CommsNotificationStore] Error in onSnapshot listener:', error)
      }
    )

    isSubscribed.value = true
  }

  function unsubscribeStore() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    unreadCommsByCustomerId.value = {}
    isSubscribed.value = false
  }

  return {
    unreadCommsByCustomerId,
    isSubscribed,
    totalUnreadCount,
    customerIdsWithUnreadComms,
    subscribe,
    unsubscribe: unsubscribeStore,
  }
})
